import Add from '@mui/icons-material/Add';
import InboxOutlined from '@mui/icons-material/InboxOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

interface SettingsTableSkeletonProps {
  columns: number;
  rows?: number;
  showHeadPlaceholder?: boolean;
}

export function SettingsTableSkeleton({ columns, rows = 5, showHeadPlaceholder }: SettingsTableSkeletonProps) {
  return (
    <TableContainer sx={{ maxHeight: { xs: 360, sm: 'none' } }}>
      <Table size="small" stickyHeader={showHeadPlaceholder}>
        {showHeadPlaceholder && (
          <TableHead>
            <TableRow>
              {Array.from({ length: columns }).map((_, j) => (
                <TableCell key={j} sx={{ bgcolor: 'action.hover' }}>
                  <Skeleton animation="wave" width="60%" />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {Array.from({ length: rows }).map((_, i) => (
            <TableRow key={i}>
              {Array.from({ length: columns }).map((_, j) => (
                <TableCell key={j}>
                  <Skeleton animation="wave" variant="text" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface SettingsEmptyTableRowProps {
  colSpan: number;
  message?: string;
}

export function SettingsEmptyTableRow({ colSpan, message = 'Мэдээлэл байхгүй' }: SettingsEmptyTableRowProps) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} align="center" sx={{ py: 6, border: 'none' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <InboxOutlined sx={{ fontSize: 40, color: 'action.disabled' }} aria-hidden />
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
}

interface SettingsListSectionProps {
  title: string;
  subtitle?: string;
  addButtonLabel?: string;
  onAdd: () => void;
  error: string | null;
  loading: boolean;
  skeletonColumns: number;
  children: ReactNode;
}

/**
 * Card + toolbar + optional list error + table area (or loading skeleton).
 */
export default function SettingsListSection({
  title,
  subtitle,
  addButtonLabel = 'Шинэ нэмэх',
  onAdd,
  error,
  loading,
  skeletonColumns,
  children,
}: SettingsListSectionProps) {
  return (
    <Card variant="outlined" elevation={0} sx={{ overflow: 'hidden' }}>
      <CardHeader
        title={title}
        subheader={subtitle}
        action={
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={onAdd} disabled={loading}>
            {addButtonLabel}
          </Button>
        }
        titleTypographyProps={{ variant: 'h6', component: 'h2' }}
        subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
        sx={{ pb: 1, '& .MuiCardHeader-action': { alignSelf: 'center', m: 0 } }}
      />
      <Divider />
      {error && (
        <Alert severity="error" sx={{ borderRadius: 0 }} role="alert">
          {error}
        </Alert>
      )}
      <Box sx={{ width: 1, overflow: 'auto' }}>
        {loading ? <SettingsTableSkeleton columns={skeletonColumns} showHeadPlaceholder /> : children}
      </Box>
    </Card>
  );
}
